import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { REPORTS_BASE_URL, DAILY_SALES_TOTALS_API } from "../../../../Constants/Config";
import { useAuthDetails } from "../../../../Common/cookiesHelper";

const initialState = {
    loading: false,
    dailySalesData: [],
    summary: {
        total_sales: 0,
        total_transactions: 0,
        avg_transaction: 0,
        days_count: 0,
    },
    status: false,
    successMessage: "",
    error: "",
    latestRequestId: null,
};

let dailySalesTotalsAbortController = null;

export const abortDailySalesTotalsRequest = () => {
    if (dailySalesTotalsAbortController) {
        dailySalesTotalsAbortController.abort();
        dailySalesTotalsAbortController = null;
    }
};

export const fetchDailySalesTotals = createAsyncThunk(
    "DailySalesTotals/fetchDailySalesTotals",
    async (data, { rejectWithValue, requestId }) => {
        if (dailySalesTotalsAbortController) {
            dailySalesTotalsAbortController.abort();
        }
        dailySalesTotalsAbortController = new AbortController();
        const signal = dailySalesTotalsAbortController.signal;

        try {
            const { LoginGetDashBoardRecordJson, userTypeData, GetSessionLogin } = useAuthDetails();
            const authToken = userTypeData?.token || LoginGetDashBoardRecordJson?.token || data?.token;
            const formData = new FormData();

            formData.append("merchant_id", data?.merchant_id || LoginGetDashBoardRecordJson?.data?.merchant_id || "");
            if (data?.outlet_ids) {
                formData.append("outlet_ids", data.outlet_ids);
            }
            formData.append("start_date", data?.start_date || "");
            formData.append("end_date", data?.end_date || "");
            formData.append("token_id", userTypeData?.token_id || "");
            formData.append("login_type", userTypeData?.login_type || "");
            formData.append("session_id", userTypeData?.session_id || "");
            formData.append("email", GetSessionLogin?.email || LoginGetDashBoardRecordJson?.data?.email || "");

            const response = await axios.post(
                REPORTS_BASE_URL + DAILY_SALES_TOTALS_API,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authToken}`,
                    },
                    signal,
                }
            );

            const daily_data = response.data.daily_data || [];
            const summary = response.data.summary || {
                total_sales: 0,
                total_transactions: 0,
                avg_transaction: 0,
                days_count: 0,
            };

            if (response.data.status === true) {
                return {
                    requestId,
                    daily_data,
                    summary,
                    status: true,
                };
            }

            return rejectWithValue({
                requestId,
                message: response.data.message || "Failed to fetch data",
                status: response.status,
                data: response.data,
            });
        } catch (error) {
            if (axios.isCancel(error) || error?.code === "ERR_CANCELED") {
                return rejectWithValue({ requestId, aborted: true });
            }
            const customError = {
                requestId,
                message: error.message,
                status: error.response ? error.response.status : "Network Error",
                data: error.response ? error.response.data : null,
            };
            return rejectWithValue(customError);
        }
    }
);

const DailySalesTotalsSlice = createSlice({
    name: "DailySalesTotals",
    initialState,
    reducers: {
        clearDailySalesData: (state) => {
            abortDailySalesTotalsRequest();
            state.dailySalesData = [];
            state.summary = {
                total_sales: 0,
                total_transactions: 0,
                avg_transaction: 0,
                days_count: 0,
            };
            state.status = false;
            state.loading = false;
            state.error = "";
            state.latestRequestId = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchDailySalesTotals.pending, (state, action) => {
            state.loading = true;
            state.error = "";
            state.latestRequestId = action.meta.requestId;
        });
        builder.addCase(fetchDailySalesTotals.fulfilled, (state, action) => {
            if (state.latestRequestId !== action.payload?.requestId) {
                return;
            }
            state.loading = false;
            state.dailySalesData = action.payload.daily_data || [];
            state.summary = action.payload.summary || {
                total_sales: 0,
                total_transactions: 0,
                avg_transaction: 0,
                days_count: 0,
            };
            state.status = action.payload.status || false;
            state.error = "";
        });
        builder.addCase(fetchDailySalesTotals.rejected, (state, action) => {
            if (state.latestRequestId !== action.meta.requestId) {
                return;
            }
            if (action.payload?.aborted) {
                return;
            }
            state.loading = false;
            state.dailySalesData = [];
            state.summary = {
                total_sales: 0,
                total_transactions: 0,
                avg_transaction: 0,
                days_count: 0,
            };
            state.status = false;
            state.error = action.payload?.message || action.error?.message || "";
        });
    },
});

export const { clearDailySalesData } = DailySalesTotalsSlice.actions;
export default DailySalesTotalsSlice.reducer;
